import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { PAGINATION_DEFAULT, SEARCH_PAGE_LIMIT } from '@/constants/pagination';


export default {
  name: 'export-tab',
  components: {},
  data() {
    return {
      selectedObjProps: {},
      activePanels: [],
      conflicts: {},
      resources: {
        entities: { items: [], loading: false, offset: 0, limit: SEARCH_PAGE_LIMIT, rowCount: 0, loadedOnce: false },
        intents: { items: [], loading: false, offset: 0, limit: SEARCH_PAGE_LIMIT, rowCount: 0, loadedOnce: false },
        dialogs: { items: [], loading: false, offset: 0, limit: SEARCH_PAGE_LIMIT, rowCount: 0, loadedOnce: false },
        workflows: { items: [], loading: false, offset: 0, limit: SEARCH_PAGE_LIMIT, rowCount: 0, loadedOnce: false },
      },
      unitMap: {
        entities: 'entity',
        intents: 'intent',
        dialogs: 'dialog',
        workflows: 'workflow',
      },
    };
  },
  computed: {
    entities() { return this.resources.entities.items },
    intents() { return this.resources.intents.items },
    dialogs() { return this.resources.dialogs.items },
    workflows() { return this.resources.workflows.items },

    selectedItemsTree() {
      const tree = {};
      const selectedEntities = this.entities.filter(item => item.show);
      if (selectedEntities.length > 0) {
        tree.entities = selectedEntities;
      }

      const selectedIntents = this.intents.filter(item => item.show);
      if (selectedIntents.length > 0) {
        tree.intents = selectedIntents;
      }

      const selectedDialogs = this.dialogs.filter(item => item.show);
      if (selectedDialogs.length > 0) {
        tree.dialogs = selectedDialogs;
      }

      const selectedWorkflows = this.workflows.filter(item => item.show);
      if (selectedWorkflows.length > 0) {
        tree.workflows = selectedWorkflows;
      }
      return tree;
    },
    selectedCount() {
      return this.entities.filter(item => item.show).length +
             this.intents.filter(item => item.show).length +
             this.dialogs.filter(item => item.show).length +
             this.workflows.filter(item => item.show).length;
    },
    hasSelections() {
      return this.selectedCount > 0;
    },
    getButtonName() {
      if (!this.hasSelections) return 'Export';
      if (this.hasUnresolvedItems) return 'Resolve Selections';
      return `Export ${this.selectedCount} item${this.selectedCount > 1 ? 's' : ''}`;
    },
    buttonEnabled() {
      if (!this.hasSelections) return false;
      if (this.hasUnresolvedItems) return true;
      return this.hasNoConflicts;
    },
    hasUnresolvedItems() {
      return Object.keys(this.selectedItemsTree).some(unitType =>
        this.selectedItemsTree[unitType].some(item => {
          const key = `${this.formatCategoryName(unitType)}:${item.name}`;
          return !Object.hasOwn(this.conflicts, key);
        }),
      );
    },

    hasNoConflicts() {
      return Object.keys(this.selectedItemsTree).every(unitType =>
        this.selectedItemsTree[unitType].every(item => {
          const key = `${this.formatCategoryName(unitType)}:${item.name}`;
          return Object.hasOwn(this.conflicts, key) && isEmpty(this.conflicts[key]);
        }),
      );
    },
  },
  methods: {
    async onPanelsChange(active) {
      const open = Array.isArray(active) ? active : [active].filter(Boolean);

      await Promise.all(open.map(key => this.ensureLoaded(key)));
    },
    async ensureLoaded(key) {
      const r = this.resources[key];
      if (!r || r.loadedOnce) {
        return;
      }
      r.loadedOnce = true;
      await this.loadMore(key);
    },
    async loadMore(key) {
      const r = this.resources[key];
      if (!r || r.loading) return;

      const isAllLoaded = r.rowCount > 0 && r.items.length >= r.rowCount;
      if (isAllLoaded) return;

      r.loading = true;
      try {
        let response;

        if (key === 'intents') {
          response = await this.$store.dispatch('intents/getAll', { q: '', limit: r.limit, offset: r.offset });
        } else if (key === 'entities') {
          response = await this.$store.dispatch('entities/getEntitiesList', [{ q: '', limit: r.limit, offset: r.offset }]);
        } else if (key === 'dialogs') {
          response = await this.$store.dispatch('dialogs/getDialogsList', [{ q: '', limit: r.limit, offset: r.offset }]);
        } else if (key === 'workflows') {
          response = await this.$store.dispatch('workflows/getWorkflowsList', [{ q: '', limit: r.limit, offset: r.offset }]);
        } else {
          this.$aiq.notify.error(`Unknown category: ${key}`);
        }

        const pagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
        const rows = get(response, 'data.data', []);

        const mapped = rows.map(x => ({ id: x.id, name: x.name, show: false }));
        r.items.push(...mapped);
        r.rowCount = pagination.rowCount ?? pagination.total ?? r.rowCount ?? 0;
        r.offset = r.items.length;
        r.limit = pagination.limit ?? r.limit;
      } catch (error) {
        this.$aiq.notify.error(`Failed to load ${key} list`);
      } finally {
        r.loading = false;
      }
    },
    formatCategoryName(category) {
      return category
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    onSelect(category, index, value) {
      const itemName = this.resources[category].items[index].name;
      const key = `${this.formatCategoryName(category)}:${itemName}`;
      if (!value) {
        delete this.selectedObjProps[key];
        if (this.conflicts[key]) {
          delete this.conflicts[key];
        }
      }
    },

    onClear() {
      this.selectedObjProps = {};
      this.conflicts = {};
      Object.values(this.resources).forEach(resource => {
        resource.items.forEach(item => { item.show = false });
      });
    },

    selectedProps(unitType, name) {
      const key = `${this.formatCategoryName(unitType)}:${name}`;
      return isEmpty(this.selectedObjProps[key]) ? [] : this.selectedObjProps[key];
    },

    extractConflicts(nodeName, exportObject) {
      const nodeMap = exportObject.items;
      const conflicts = [];
      for (const unitType of Object.keys(nodeMap)) {
        for (const obj of Object.values(nodeMap[unitType])) {
          const parent = `${this.formatCategoryName(unitType)}:${obj.name}`;
          if (!isEmpty(obj['conflicts'])) {
            conflicts.push(...obj['conflicts'].map(conflict => `${parent}-${conflict.type}:${conflict.name}`));
          }
        }
      }
      this.conflicts = { ...this.conflicts, [nodeName]: conflicts };
      return conflicts;
    },

    async onExport() {
      if (this.hasUnresolvedItems) {
        for (const unitType of Object.keys(this.selectedItemsTree)) {
          for (const unit of this.selectedItemsTree[unitType]) {
            try {
              const unit_type = this.unitMap[unitType];
              const response = await this.$store.dispatch('migrates/export', { id: unit['id'], unitType: unit_type });
              const nodeName = `${this.formatCategoryName(unitType)}:${unit.name}`;
              this.selectedObjProps[nodeName] = this.extractConflicts(nodeName, response);
            } catch (error) {
              this.$aiq.notify.error(`Failed to resolve: ${unitType} ${unit.name}`);
            }
          }
        }
      } else {
        const exportedObjects = [];
        for (const unitType of Object.keys(this.selectedItemsTree)) {
          for (const unit of this.selectedItemsTree[unitType]) {
            const unit_type = this.unitMap[unitType];
            try {
              const response = await this.$store.dispatch('migrates/export', { id: unit['id'], unitType: unit_type });
              exportedObjects.push(response);
            } catch (error) {
              this.$aiq.notify.error(`Failed to export: ${unitType} ${unit.name}`);
            }
          }
        }
        const blob = new Blob([JSON.stringify(exportedObjects, null)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'exportedObjects.json';
        link.click();
        URL.revokeObjectURL(url);
      }
    },
  },
};
